import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/axiosInstance";

const parseJwt = (token) => {
  try { return JSON.parse(atob(token.split(".")[1])); } catch { return null; }
};

const buildUser = (decoded) => ({
  id:                 decoded.sub,
  subAccountId:       decoded.accountType === 'subaccount'
                        ? (parseInt(String(decoded.sub || '').replace('sa-', ''), 10) || null)
                        : null,
  name:               decoded.name,
  email:              decoded.email,
  plan:               decoded.plan,
  role:               decoded.role,
  accountType:        decoded.accountType        || "superuser",
  extension:          decoded.extension          || null,
  permissions:        (decoded.accountType === 'superuser' || !decoded.accountType)
                        ? 'view,edit,analytics,billing'
                        : (decoded.permissions || 'view'),
  mustChangePassword: decoded.mustChangePassword || false,
});

const tokenFromStorage = localStorage.getItem("token");
const decoded          = tokenFromStorage ? parseJwt(tokenFromStorage) : null;

const initialState = {
  token:       tokenFromStorage || null,
  user:        decoded ? buildUser(decoded) : null,
  userDetails: null,
  agentPerms:  null,   // null = admin (unrestricted); object = subaccount's effective perms
};

export const fetchMe = createAsyncThunk("auth/fetchMe", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/User/me");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchAgentPerms = createAsyncThunk("auth/fetchAgentPerms", async (_, thunkAPI) => {
  const state = thunkAPI.getState();
  if (state.auth.user?.accountType !== 'subaccount') return null;
  try {
    const res = await axiosInstance.get("/crm/v2/my-perms");
    return res.data.perms || null;
  } catch {
    return null;
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { token } = action.payload;
      const dec = parseJwt(token);
      state.token = token;
      state.user  = buildUser(dec);
      localStorage.setItem("token", token);
    },
    clearMustChange: (state) => {
      if (state.user) state.user.mustChangePassword = false;
    },
    logout: (state) => {
      state.token       = null;
      state.user        = null;
      state.userDetails = null;
      state.agentPerms  = null;
      localStorage.removeItem("token");
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMe.fulfilled, (state, action) => {
      state.userDetails = action.payload;
      if (state.user) {
        if (action.payload.mustChangePassword !== undefined)
          state.user.mustChangePassword = action.payload.mustChangePassword;
        if (action.payload.permissions)
          state.user.permissions = action.payload.permissions;
      }
    });
    builder.addCase(fetchAgentPerms.fulfilled, (state, action) => {
      state.agentPerms = action.payload;
    });
  },
});

export const { setCredentials, clearMustChange, logout } = authSlice.actions;
export const selectAgentPerms = (state) => state.auth.agentPerms;
export default authSlice.reducer;
